import { isBefore, parseISO } from 'date-fns'
import {
  type InferOutput,
  array,
  boolean,
  forward,
  nullable,
  number,
  object,
  partialCheck,
  pipe,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { type ToursListExEntity } from '@/libs/api'

import {
  mapToToursChargeAdditionalPaymentFormValue,
  ToursChargeAdditionalPaymentFormValueSchema
} from './tours-charge-additional-payments-form-value'
import { ToursChargeCustomerChargeFormValueSchema } from './tours-charge-customer-charge-form-value'
import {
  mapToToursRouteFormValue,
  toursRouteFormValueSchema
} from './tours-route-form-value'

/**
 * 運行実績の行単位のフォームスキーマ
 */
export const tourEditFormValueSchema = pipe(
  object({
    /** 運行実績ID */
    tourId: number(),
    /** 修正理由 */
    editReason: string(),
    /** 業務開始日時 */
    startAt: string(),
    /** 業務終了日時 */
    endAt: string(),
    /** 編集中かどうか */
    isEditing: boolean(),
    /** 配送ルート */
    details: array(toursRouteFormValueSchema),
    /** 距離(km) */
    distance: nullable(number()),
    /** 証跡ファイル */
    tourEvidenceFiles: array(
      object({
        key: string(),
        name: string()
      })
    ),
    /** 追加支払料金 */
    additionalPayments: array(ToursChargeAdditionalPaymentFormValueSchema),
    /** 請求額 */
    customerCharges: array(ToursChargeCustomerChargeFormValueSchema),
    contractType: nullable(string()),
    /** 車両 */
    truckId: nullable(number()),
    /** 備考 */
    note: nullable(string())
  }),
  forward(
    partialCheck(
      [['isEditing'], ['editReason']],
      // 編集中の場合は修正理由は必須
      (input) => input.isEditing === false || input.editReason.length > 0,
      VALIDATION_MESSAGES.REQUIRED_ITEM
    ),
    ['editReason']
  ),
  forward(
    partialCheck(
      [['startAt'], ['endAt'], ['isEditing']],
      ({ startAt, endAt, isEditing }) =>
        // 編集中の場合は業務時間を比較
        isEditing === false || isBefore(parseISO(startAt), parseISO(endAt)),
      VALIDATION_MESSAGES.AFTER_START_DATE
    ),
    ['endAt']
  )
)

/**
 * 運行実績の行単位のフォーム値
 */
export type TourEditFormValue = InferOutput<typeof tourEditFormValueSchema>

/**
 * 運行実績の一括編集用のフォームスキーマ
 */
export const toursBulkEditFormValuesSchema = object({
  tours: array(tourEditFormValueSchema)
})

/**
 * 運行実績の一括編集用のフォーム値
 */
export type ToursBulkEditFormValues = InferOutput<
  typeof toursBulkEditFormValuesSchema
>

/**
 * 運行実績Entityを行単位のフォーム値に変換する
 */
export const mapToTourEditFormValue = ({
  id,
  startAt,
  endAt,
  details,
  distance,
  tourEvidenceFiles,
  charge,
  // パートナーユーザーの応答には請求側（customerCharges）が無い
  customerCharges = [],
  truckId
}: ToursListExEntity): TourEditFormValue => {
  return {
    tourId: id,
    isEditing: false,
    // 修正理由は空文字で初期化
    editReason: '',
    startAt,
    endAt,
    distance,
    details: details.map(mapToToursRouteFormValue),
    tourEvidenceFiles: tourEvidenceFiles.map((file) => ({
      key: file.tourEvidenceFileKey,
      name: file.tourEvidenceFileName
    })),
    additionalPayments: charge
      ? charge.additionalPayments.map(
          mapToToursChargeAdditionalPaymentFormValue
        )
      : [],
    customerCharges: customerCharges.map((x) => ({
      customerChargeId: x.id,
      billedPrice: x.billedPrice ?? 0,
      // #3328 VOC_PJ-761: 一括編集では税区分 UI を出さないが、既存値を保持して送信する
      taxCategoryId: x.taxCategoryId ?? null,
      // VOC_PJ-844 #3624: 内税フラグも UI を出さないが、既存値を保持して送信する
      isTaxInclusive: x.isTaxInclusive ?? null,
      // VOC_PJ-888 #3917: 端数処理も UI を出さないが、既存値を保持して送信する
      roundType: x.roundType ?? null,
      otherBilledPrice: x.otherBilledPrice ?? 0,
      otherBilledTaxCategoryId: x.otherBilledTaxCategoryId ?? null,
      additionalCharges: [],
      customerContractId: null,
      truckModelId: null,
      totalWeight: null,
      // VOC_PJ-55 T-3: 一括編集では計上日は触らない (= 旧値を引き継ぐため null)
      operatingDate: null,
      // VOC_PJ-960 #4487: 一括編集は customKey UI を出さないが、既存値を保持して送信する
      // （税区分等と同規約。BE は同値なら実質引き継ぎ）
      customKey1: x.customKey1 ?? null,
      customKey2: x.customKey2 ?? null,
      customKey3: x.customKey3 ?? null,
      customKey4: x.customKey4 ?? null,
      customKey5: x.customKey5 ?? null
    })),
    contractType: charge?.contract?.type ?? null,
    truckId,
    note: null
  }
}
